/**
 * AssociatedRecordsSection Component
 * Displays invoices, deliveries, batch allocations, and payment status
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileText, Truck, Package, CreditCard, ExternalLink } from 'lucide-react';
import type {
  InvoiceReference,
  DeliveryReference,
  BatchAllocation,
  PaymentDetails,
} from '@/types/order';

interface AssociatedRecordsSectionProps {
  invoice?: InvoiceReference;
  delivery?: DeliveryReference;
  batchAllocations: BatchAllocation[];
  payment: PaymentDetails;
}

/**
 * Format currency
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
}

/**
 * Format date
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function AssociatedRecordsSection({
  invoice,
  delivery,
  batchAllocations,
  payment,
}: AssociatedRecordsSectionProps) {
  return (
    <div className="space-y-6">
      {/* Invoice & Delivery References */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Invoice */}
        {invoice && (
          <Card className="border-slate-200 bg-white shadow-md">
            <CardHeader className="pb-3 border-b border-slate-200">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600" />
                Invoice
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Invoice Number</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {invoice.invoiceNumber}
                  </p>
                </div>
                {invoice.invoiceUrl && (
                  <a
                    href={invoice.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500">Invoice Date</p>
                <p className="text-sm font-medium text-slate-900">
                  {formatDate(invoice.invoiceDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Invoice Amount</p>
                <p className="text-lg font-bold text-purple-900">
                  {formatCurrency(invoice.invoiceAmount)}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delivery */}
        {delivery && (
          <Card className="border-slate-200 bg-white shadow-md">
            <CardHeader className="pb-3 border-b border-slate-200">
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="h-5 w-5 text-blue-600" />
                Delivery
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Delivery Number</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {delivery.deliveryNumber}
                  </p>
                </div>
                {delivery.deliveryUrl && (
                  <a
                    href={delivery.deliveryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500">Delivery Date</p>
                <p className="text-sm font-medium text-slate-900">
                  {formatDate(delivery.deliveryDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Status</p>
                <Badge
                  variant={
                    delivery.deliveryStatus === 'DELIVERED'
                      ? 'default'
                      : delivery.deliveryStatus === 'IN_TRANSIT'
                      ? 'secondary'
                      : 'outline'
                  }
                >
                  {delivery.deliveryStatus}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Batch Allocations */}
      <Card className="border-slate-200 bg-white shadow-md">
        <CardHeader className="pb-3 border-b border-slate-200">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="h-5 w-5 text-green-600" />
            Batch Allocations ({batchAllocations.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-700">Batch Number</TableHead>
                  <TableHead className="font-semibold text-slate-700">Product Type</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Quantity</TableHead>
                  <TableHead className="font-semibold text-slate-700">Allocation Date</TableHead>
                  <TableHead className="font-semibold text-slate-700">Expiry Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batchAllocations.map((allocation) => (
                  <TableRow key={allocation.allocationId} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-slate-900">
                      {allocation.batchNumber}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{allocation.productType}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {allocation.allocatedQuantity} {allocation.quantityUnit}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {formatDate(allocation.allocationDate)}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {allocation.expiryDate ? formatDate(allocation.expiryDate) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Payment Status */}
      <Card className="border-slate-200 bg-white shadow-md">
        <CardHeader className="pb-3 border-b border-slate-200">
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-amber-600" />
            Payment Status
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700 font-medium">Total Amount</p>
              <p className="text-lg font-bold text-blue-900">
                {formatCurrency(payment.totalAmount)}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700 font-medium">Paid Amount</p>
              <p className="text-lg font-bold text-green-900">
                {formatCurrency(payment.paidAmount)}
              </p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-xs text-red-700 font-medium">Balance Due</p>
              <p className="text-lg font-bold text-red-900">
                {formatCurrency(payment.balanceDue)}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-700 font-medium">Status</p>
              <Badge
                variant={
                  payment.paymentStatus === 'PAID'
                    ? 'default'
                    : payment.paymentStatus === 'PARTIAL'
                    ? 'secondary'
                    : 'destructive'
                }
                className="mt-1"
              >
                {payment.paymentStatus}
              </Badge>
            </div>
          </div>
          {payment.lastPaymentDate && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-600">Last Payment Date</p>
                <p className="text-sm font-medium text-slate-900">
                  {formatDate(payment.lastPaymentDate)}
                </p>
              </div>
              {payment.nextDueDate && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-700">Next Due Date</p>
                  <p className="text-sm font-medium text-amber-900">
                    {formatDate(payment.nextDueDate)}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
